import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import GLAccountService from "services/Setup/GLAccount/GLAccountService";
import { notification } from "antd";

export const initialState = {
  loading: false,
  glAccountList: [],
  glAccountCount: 0,
  selectedGLAccount: null,
};

// Normalizes the paginated { pageIndex, pageSize, count, data } response.
const toListAndCount = (response) => {
  const payload = response?.data;
  if (payload && Array.isArray(payload.data)) {
    return { list: payload.data, count: payload.count ?? payload.data.length };
  }
  if (Array.isArray(payload)) return { list: payload, count: payload.length };
  if (Array.isArray(payload?.result)) return { list: payload.result, count: payload.result.length };
  return { list: [], count: 0 };
};

// Also used for the approval list (filtered client-side by flagData) since
// the backend has no dedicated searchapproval endpoint for GL Account.
export const getGLAccountList = createAsyncThunk(
  "glAccount/getGLAccountList",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await GLAccountService.getGLAccounts(params);
      return toListAndCount(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// No dedicated ref/{code} endpoint either — search by the business key and
// take the first match, same approach the old finstrust_lms source used.
export const getGLAccountById = createAsyncThunk(
  "glAccount/getGLAccountById",
  async (glAccountId, { rejectWithValue }) => {
    try {
      const response = await GLAccountService.getGLAccounts({
        glaccountid: glAccountId,
        description: "",
      });
      const { list } = toListAndCount(response);
      return list.find((x) => x.glAccountId === glAccountId) ?? list[0] ?? null;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const addGLAccount = createAsyncThunk(
  "glAccount/addGLAccount",
  async (userInput, { rejectWithValue }) => {
    try {
      const response = await GLAccountService.addGLAccount(userInput);

      if (response.status === 200) {
        const data = response.data;
        if (data.status === "Success") {
          notification.success({
            message: data.status,
            description: data.message,
            duration: 3,
          });
        } else {
          notification.error({
            message: data.status,
            description: data.message,
            duration: 3,
          });
        }
        return data;
      } else {
        notification.error({
          message: "Error",
          description: "Unexpected response!",
          duration: 3,
        });
        return rejectWithValue({
          status: "Error",
          response: response?.data?.message || "Unexpected response!",
        });
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Unexpected error!");
    }
  }
);

export const updateGLAccount = createAsyncThunk(
  "glAccount/updateGLAccount",
  async (data, { rejectWithValue }) => {
    try {
      const response = await GLAccountService.updateGLAccount(data);

      if (response.status === 200) {
        notification.success({
          message: response.data.status,
          description: response.data.message,
          duration: 3,
        });
        return response.data;
      }

      return rejectWithValue(response.data);
    } catch (error) {
      notification.error({
        message: error.response?.status,
        description: error.response?.statusText,
      });
      return rejectWithValue(error.response?.data);
    }
  }
);

export const deleteGLAccount = createAsyncThunk(
  "glAccount/deleteGLAccount",
  async (data, { rejectWithValue }) => {
    try {
      const response = await GLAccountService.deleteGLAccount(data);

      if (response.status === 200) {
        notification.success({
          message: response.data.status,
          description: response.data.message,
        });
        return response.data;
      }

      return rejectWithValue(response.data);
    } catch (error) {
      notification.error({
        message: "Error",
        description: error.response?.data?.message || "Delete failed",
      });
      return rejectWithValue(error.response?.data);
    }
  }
);

export const approveGLAccount = createAsyncThunk(
  "glAccount/approveGLAccount",
  async (data, { rejectWithValue }) => {
    try {
      const response = await GLAccountService.approveGLAccount(data);

      if (response.status === 200) {
        notification.success({
          message: response.data.status,
          description: response.data.message,
        });
        return response.data;
      }

      return rejectWithValue(response.data);
    } catch (error) {
      notification.error({
        message: "Error",
        description: error.response?.data?.message || "Approve failed",
      });
      return rejectWithValue(error.response?.data);
    }
  }
);

export const rejectGLAccount = createAsyncThunk(
  "glAccount/rejectGLAccount",
  async (data, { rejectWithValue }) => {
    try {
      const response = await GLAccountService.rejectGLAccount(data);

      if (response.status === 200) {
        notification.success({
          message: response.data.status,
          description: response.data.message,
        });
        return response.data;
      }

      return rejectWithValue(response.data);
    } catch (error) {
      notification.error({
        message: "Error",
        description: error.response?.data?.message || "Reject failed",
      });
      return rejectWithValue(error.response?.data);
    }
  }
);

export const returnGLAccount = createAsyncThunk(
  "glAccount/returnGLAccount",
  async (data, { rejectWithValue }) => {
    try {
      const response = await GLAccountService.returnGLAccount(data);

      if (response.status === 200) {
        notification.success({
          message: response.data.status,
          description: response.data.message,
        });
        return response.data;
      }

      return rejectWithValue(response.data);
    } catch (error) {
      notification.error({
        message: "Error",
        description: error.response?.data?.message || "Return failed",
      });
      return rejectWithValue(error.response?.data);
    }
  }
);

export const requestGLAccount = createAsyncThunk(
  "glAccount/requestGLAccount",
  async (data, { rejectWithValue }) => {
    try {
      const response = await GLAccountService.requestGLAccount(data);

      if (response.status === 200) {
        notification.success({
          message: response.data.status,
          description: response.data.message,
        });
        return response.data;
      }

      return rejectWithValue(response.data);
    } catch (error) {
      notification.error({
        message: "Error",
        description: error.response?.data?.message || "Request failed",
      });
      return rejectWithValue(error.response?.data);
    }
  }
);

const GLAccountSlice = createSlice({
  name: "glAccount",
  initialState,
  reducers: {
    clearSelectedGLAccount: (state) => {
      state.selectedGLAccount = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getGLAccountList.pending, (state) => {
        state.loading = true;
      })
      .addCase(getGLAccountList.fulfilled, (state, action) => {
        state.loading = false;
        state.glAccountList = action.payload.list;
        state.glAccountCount = action.payload.count;
      })
      .addCase(getGLAccountList.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getGLAccountById.pending, (state) => {
        state.loading = true;
      })
      .addCase(getGLAccountById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedGLAccount = action.payload;
      })
      .addCase(getGLAccountById.rejected, (state) => {
        state.loading = false;
      })
      .addCase(addGLAccount.pending, (state) => {
        state.loading = true;
      })
      .addCase(addGLAccount.fulfilled, (state, action) => {
        state.loading = false;
        state.result = action.payload;
        state.showMessage = true;
        state.message = action.payload?.message;
        state.statusRespon = 200;
        state.redirect = "/";
      })
      .addCase(addGLAccount.rejected, (state, action) => {
        state.loading = false;
        state.result = action.payload;
        state.showMessage = true;
        state.statusRespon = "Error";
        state.message = action.payload?.message || "Failed to save data.";
      })
      .addCase(updateGLAccount.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateGLAccount.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateGLAccount.rejected, (state) => {
        state.loading = false;
      })

      .addCase(deleteGLAccount.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteGLAccount.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteGLAccount.rejected, (state) => {
        state.loading = false;
      })
      // APPROVE
      .addCase(approveGLAccount.pending, (state) => {
        state.loading = true;
      })
      .addCase(approveGLAccount.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(approveGLAccount.rejected, (state) => {
        state.loading = false;
      })

      // REJECT
      .addCase(rejectGLAccount.pending, (state) => {
        state.loading = true;
      })
      .addCase(rejectGLAccount.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(rejectGLAccount.rejected, (state) => {
        state.loading = false;
      })

      // RETURN
      .addCase(returnGLAccount.pending, (state) => {
        state.loading = true;
      })
      .addCase(returnGLAccount.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(returnGLAccount.rejected, (state) => {
        state.loading = false;
      })

      // REQUEST
      .addCase(requestGLAccount.pending, (state) => {
        state.loading = true;
      })
      .addCase(requestGLAccount.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(requestGLAccount.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const { clearSelectedGLAccount } = GLAccountSlice.actions;

export default GLAccountSlice.reducer;
